/**
 * Storefront PromoStrip — compact discovery strip for active promotions.
 *
 * Reads `/api/bundle/quote` (public) and renders up to two independent
 * offer panels. Visibility is driven purely by admin settings:
 *   - `quote.five_for_five.enabled`  → 5 FOR 5 panel
 *   - `quote.enabled` (bundle)       → ALL PREMIUM panel
 *
 * The two offers are deliberately presented side-by-side as SEPARATE
 * promos — they do not stack. Copy is localized (EN / AR) via i18next.
 * Component renders nothing if neither promo is active.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";

export default function PromoStrip() {
  const { t } = useTranslation();
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/bundle/quote")
      .then((r) => { if (alive) setQuote(r.data || null); })
      .catch(() => { if (alive) setQuote(null); });
    return () => { alive = false; };
  }, []);

  if (!quote) return null;
  const showFF = !!quote?.five_for_five?.enabled;
  const showBundle = !!quote?.enabled;
  if (!showFF && !showBundle) return null;

  const ffPct = Number(quote?.five_for_five?.discount_percent ?? 5);
  const ffMin = Number(quote?.five_for_five?.min_tracks ?? 5);
  const bundlePct = Number(quote?.discount_percent ?? 10);

  const panels = [];
  if (showFF) {
    panels.push(
      <Panel
        key="ff"
        testid="promo-strip-fiveforfive"
        kicker={t("promotions.strip.fiveForFive.kicker")}
        headline={t("promotions.strip.fiveForFive.headline", { count: ffMin, percent: ffPct })}
        body={t("promotions.strip.fiveForFive.body", { count: ffMin, percent: ffPct })}
        badge={`${ffPct}% OFF`}
      />
    );
  }
  if (showBundle) {
    panels.push(
      <Panel
        key="bundle"
        testid="promo-strip-bundle"
        kicker={t("promotions.strip.bundle.kicker")}
        headline={t("promotions.strip.bundle.headline", { percent: bundlePct })}
        body={t("promotions.strip.bundle.body", { percent: bundlePct })}
        badge={`${bundlePct}% OFF`}
      />
    );
  }

  return (
    <div
      data-testid="storefront-promo-strip"
      className={`grid grid-cols-1 ${panels.length > 1 ? "md:grid-cols-2" : ""} gap-3 mb-6`}
    >
      {panels}
    </div>
  );
}

function Panel({ testid, kicker, headline, body, badge }) {
  return (
    <div
      data-testid={testid}
      className="hairline rounded-xl bg-white/[0.02] border-red-brand/25 hover:border-red-brand/60 transition-colors px-4 py-3 flex items-center gap-3"
    >
      <div className="shrink-0 font-mono text-[10px] tracking-[0.28em] text-black bg-red-brand rounded-full px-2.5 py-1">
        {badge}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] tracking-[0.3em] text-red-brand/90 uppercase truncate">
          {kicker}
        </div>
        <div className="text-white text-sm sm:text-[15px] font-semibold leading-tight truncate">
          {headline}
        </div>
        <div className="text-white/60 text-xs mt-0.5 truncate">
          {body}
        </div>
      </div>
    </div>
  );
}
